import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: SelectOption[];
  // Optional extra groups rendered below the main options under headings
  // (e.g. "All other sites"). Searchable and selectable like main options.
  extraGroups?: Array<{ heading: string; options: SelectOption[] }>;
  triggerWidth?: string;
  dropdownWidth?: string;
}

export function SearchableSelect({
  value,
  onValueChange,
  placeholder,
  options,
  extraGroups,
  triggerWidth = "140px",
  dropdownWidth = "220px",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const isActive = value !== "__all";
  const allOptions = [...options, ...(extraGroups?.flatMap((g) => g.options) ?? [])];
  const selectedLabel = allOptions.find((o) => o.value === value)?.label ?? placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-7 items-center justify-between gap-1.5 rounded-md border border-black/15 bg-white px-2.5 text-[13px] transition-colors hover:bg-gray-50",
            isActive &&
              "border-[var(--brand-dark-teal)] font-semibold text-[var(--brand-dark-teal)]"
          )}
        >
          <span className="truncate" style={{ maxWidth: triggerWidth }}>
            {selectedLabel}
          </span>
          <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: dropdownWidth }} align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {options.length > 0 && (
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => {
                      onValueChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "size-3 shrink-0",
                        value === opt.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {extraGroups?.map(
              (group) =>
                group.options.length > 0 && (
                  <CommandGroup key={group.heading} heading={group.heading}>
                    {group.options.map((opt) => (
                      <CommandItem
                        key={opt.value}
                        value={opt.label}
                        onSelect={() => {
                          onValueChange(opt.value);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "size-3 shrink-0",
                            value === opt.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {opt.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
